import { CarIcon, UmbrellaIcon } from '@restworld/utils-common';
import { Alert } from '@restworld/ui-ds-base';
import { PresentationCard } from '@restworld/ui-components';
import { CardWrapper } from './card-wrapper';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { workerTransportationDetailsAtom } from '../global-state.worker-data';
import { atom, useAtomValue } from 'jotai';
import { WorkerDataLoader } from './worker-data-loader';
import { useProfileCommonIntl } from './hooks';
import { useFetchWorkerDataLoading } from '../../hooks';

const drivingLicenceSummaryAtom = atom((get) => {
  const transportationDetails = get(workerTransportationDetailsAtom);
  const drivingLicences =
    transportationDetails.driving_licences
      ?.map((licence) => licence?.driving_license_var?.label)
      .filter((label) => label) || [];

  const drivingLicenceSummary = drivingLicences.length > 0 ? drivingLicences.join(', ') : undefined;
  return drivingLicenceSummary;
});

const transportationOptionsAtom = atom((get) => {
  const transportationDetails = get(workerTransportationDetailsAtom);
  const transportationOptions =
    transportationDetails?.transportation_options
      ?.map((option) => option?.transportation_var?.label)
      ?.filter((label) => label) || [];
  return transportationOptions.length > 0 ? transportationOptions.join(', ') : undefined;
});

const displacementAvailabilityAtom = atom((get) => {
  const transportationDetails = get(workerTransportationDetailsAtom);
  return transportationDetails?.displacement_availability_var?.label;
});

const displacementPreferenceSummaryAtom = atom((get) => {
  const transportationDetails = get(workerTransportationDetailsAtom);
  const displacementPreferences =
    transportationDetails?.displacement_preferences
      ?.map((preference) => preference?.displacement_preference_var?.label)
      .filter((preference) => preference) || [];
  const displacementPreferenceSummary =
    displacementPreferences.length > 0 ? displacementPreferences.join(', ') : undefined;
  return displacementPreferenceSummary;
});

export const MovementsSection = () => {
  const workerDataLoading = useFetchWorkerDataLoading();
  const drivingLicenceSummary = useAtomValue(drivingLicenceSummaryAtom);
  const transportationOptions = useAtomValue(transportationOptionsAtom);
  const displacementAvailability = useAtomValue(displacementAvailabilityAtom);
  const displacementPreferenceSummary = useAtomValue(displacementPreferenceSummaryAtom);
  const { isWorkerMovementInfoMissing: isInformationMissing } = useIsWorkerMovementInfoMissing();

  const { missingInfoText, missingInfo: errorMessage, translate } = useProfileCommonIntl();

  return (
    <CardWrapper title={translate('onboarding-page:movements')} editPageType="transportation">
      <div id="transportation" className="flex flex-col items-start pt-6">
        {workerDataLoading && <WorkerDataLoader />}
        {isInformationMissing && !workerDataLoading && <Alert text={missingInfoText} className="mb-4" />}
        {!workerDataLoading && (
          <div className="flex lg:flex-row flex-col lg:gap-8 gap-4 w-full">
            <div className="flex flex-col items-start  justify-start lg:w-1/2 w-full gap-4">
              <PresentationCard
                label={translate('onboarding-page:patents')}
                header={drivingLicenceSummary || errorMessage}
                isError={!drivingLicenceSummary}
                icon={<CarIcon />}
              />
              <PresentationCard
                label={translate('onboarding-page:means_of_transportation')}
                header={transportationOptions || errorMessage}
                isError={!transportationOptions}
                icon={<CarIcon />}
              />
            </div>
            <div className="flex flex-col items-start  justify-start lg:w-1/2 w-full gap-4">
              <PresentationCard
                label={translate('onboarding-page:work_outside_the_city')}
                header={displacementAvailability || errorMessage}
                isError={!displacementAvailability}
                icon={<UmbrellaIcon />}
              />
              <PresentationCard
                label={translate('common:preferences')}
                header={displacementPreferenceSummary || errorMessage}
                isError={!displacementPreferenceSummary}
                icon={<MapPinIcon className="w-6 h-6" />}
              />
            </div>
          </div>
        )}
      </div>
    </CardWrapper>
  );
};

export function useIsWorkerMovementInfoMissing() {
  const drivingLicenceSummary = useAtomValue(drivingLicenceSummaryAtom);
  const transportationOptions = useAtomValue(transportationOptionsAtom);
  const displacementAvailability = useAtomValue(displacementAvailabilityAtom);
  const displacementPreferenceSummary = useAtomValue(displacementPreferenceSummaryAtom);
  const isWorkerMovementInfoMissing =
    !displacementAvailability || !transportationOptions || !displacementPreferenceSummary || !drivingLicenceSummary;

  return { isWorkerMovementInfoMissing };
}
